/*!
 * Landmark Photography — Reinvented Front End
 * Preloader, magnetic cursor, kinetic type reveal, grain texture, and an
 * optional GSAP-powered pinned hero reveal on desktop. Every feature is
 * isolated in its own try/catch and checks for its dependency before
 * running, so a blocked CDN script or an unsupported browser just means
 * fewer effects, never a broken site.
 *
 * NOTE: the Lenis smooth-scroll feature has been removed (14 Aug 2026)
 * because it was making page scrolling feel staggered/choppy. Scrolling
 * is now native/default on all devices.
 */
(function () {
  "use strict";

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isDesktop =
    window.matchMedia &&
    window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 750px)").matches;

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  ready(function () {
    try {
      initPreloader();
    } catch (e) {}
    try {
      initGrain();
    } catch (e) {}
    try {
      initReveal();
    } catch (e) {}
    try {
      initKineticHeadings();
    } catch (e) {}
    try {
      initReticleFrames();
    } catch (e) {}
    try {
      initBlurUp();
    } catch (e) {}
    try {
      initHeaderSolidify();
    } catch (e) {}
    if (isDesktop && !reduceMotion) {
      try {
        initCursor();
      } catch (e) {}
      try {
        initTilt();
      } catch (e) {}
    }
  });

  /* ------------------------------------------------------------------
     Preloader — home page only, plays on every load/refresh of "/".
     "LANDMARK" reveals letter by letter above a progress bar, with a
     small camera icon that fires its shutter and flashes right as the
     bar reaches full, the flash casting across the whole screen,
     before the wordmark wipes away into the site.
     ------------------------------------------------------------------ */
  function initPreloader() {
    var isHome = window.location.pathname === "/";

    if (!isHome || reduceMotion) return;

    var el = document.createElement("div");
    el.id = "lmk-preloader";

    var cam = document.createElement("div");
    cam.className = "lmk-preloader-camera";
    cam.setAttribute("aria-hidden", "true");
    cam.innerHTML =
      '<svg viewBox="0 0 240 160" xmlns="http://www.w3.org/2000/svg">' +
      '<rect x="30" y="50" width="180" height="88" rx="10" fill="#191818" stroke="#f5f5f5" stroke-width="4"/>' +
      '<path d="M92,50 L100,24 L140,24 L148,50 Z" fill="#191818" stroke="#f5f5f5" stroke-width="4" stroke-linejoin="round"/>' +
      '<rect class="lmk-cam-flash-head" x="104" y="4" width="32" height="18" rx="2" fill="#191818" stroke="#f5f5f5" stroke-width="3"/>' +
      '<circle cx="195" cy="38" r="7" fill="#dd4242" stroke="#f5f5f5" stroke-width="2"/>' +
      '<circle cx="120" cy="100" r="44" fill="#191818" stroke="#f5f5f5" stroke-width="4"/>' +
      '<circle cx="120" cy="100" r="34" fill="none" stroke="#f5f5f5" stroke-width="2" opacity="0.55"/>' +
      '<circle cx="120" cy="100" r="24" fill="none" stroke="#f5f5f5" stroke-width="2" opacity="0.55"/>' +
      '<circle class="lmk-cam-iris" cx="120" cy="100" r="13" fill="#191818" stroke="#dd4242" stroke-width="3"/>' +
      "</svg>";

    var word = document.createElement("div");
    word.className = "lmk-preloader-word";
    "LANDMARK".split("").forEach(function (ch, i) {
      var span = document.createElement("span");
      span.textContent = ch;
      span.style.animationDelay = 0.25 + i * 0.035 + "s";
      word.appendChild(span);
    });
    var bar = document.createElement("div");
    bar.className = "lmk-preloader-bar";
    el.appendChild(cam);
    el.appendChild(word);
    el.appendChild(bar);
    document.body.appendChild(el);
    document.body.classList.add("lmk-preloading");

    var flash = document.createElement("div");
    flash.id = "lmk-preloader-flash";
    flash.setAttribute("aria-hidden", "true");
    document.body.appendChild(flash);

    window.setTimeout(function () {
      el.classList.add("lmk-preloader-out");
      document.body.classList.remove("lmk-preloading");
      window.setTimeout(function () {
        el.remove();
        flash.remove();
      }, 1000);
    }, 2200);
  }

  /* ------------------------------------------------------------------
     Grain overlay
     ------------------------------------------------------------------ */
  function initGrain() {
    if (reduceMotion) return;
    var g = document.createElement("div");
    g.id = "lmk-grain";
    document.body.appendChild(g);
  }

  /* ------------------------------------------------------------------
     Scroll reveal for whole sections
     ------------------------------------------------------------------ */
  function initReveal() {
    var sections = Array.prototype.slice.call(
      document.querySelectorAll(".shopify-section")
    );
    if (!sections.length) return;

    if (!("IntersectionObserver" in window) || reduceMotion) {
      sections.forEach(function (s) {
        s.classList.add("lmk-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("lmk-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    sections.forEach(function (section, index) {
      if (index === 0) return;
      if (/header|footer|aside/i.test(section.id)) return;
      section.classList.add("lmk-reveal");
      observer.observe(section);
    });
  }

  /* ------------------------------------------------------------------
     Kinetic headings — word-by-word rise, only for plain-text h1/h2
     ------------------------------------------------------------------ */
  function initKineticHeadings() {
    var headings = document.querySelectorAll(".shopify-section h1, .shopify-section h2");
    if (!headings.length) return;

    var targets = [];
    headings.forEach(function (h) {
      if (h.children.length > 0) return; // don't touch headings with nested markup
      var text = h.textContent.trim();
      if (!text) return;
      var words = text.split(/\s+/);
      h.textContent = "";
      words.forEach(function (word, i) {
        var wrap = document.createElement("span");
        wrap.className = "lmk-split-word";
        var inner = document.createElement("span");
        inner.textContent = word;
        inner.style.transitionDelay = i * 0.045 + "s";
        wrap.appendChild(inner);
        h.appendChild(wrap);
        h.appendChild(document.createTextNode(" "));
      });
      targets.push(h);
    });

    if (!targets.length) return;

    if (!("IntersectionObserver" in window) || reduceMotion) {
      targets.forEach(function (h) {
        h.classList.add("lmk-kinetic-in");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("lmk-kinetic-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    targets.forEach(function (h) {
      observer.observe(h);
    });
  }

  /* ------------------------------------------------------------------
     Viewfinder-style corner brackets on portfolio images. Does NOT
     insert any new wrapper — only adds a class to the image's existing
     parent (position: relative has zero effect on box size/layout) so
     the corner brackets can be absolutely positioned via ::before/::after.
     ------------------------------------------------------------------ */
  function initReticleFrames() {
    var images = document.querySelectorAll(
      '[id*="hotspot" i] img, [id*="hero_slideshow" i] img, [id*="hero-slideshow" i] img'
    );
    images.forEach(function (img) {
      var host = img.parentElement;
      if (!host) return;
      host.classList.add("lmk-frame");
    });
  }

  /* ------------------------------------------------------------------
     Blur-up image reveal — every photo on every page (header/footer
     excluded) loads softly blurred and sharpens into focus once it's
     actually finished loading. Only adds classes to the existing <img>
     itself, never a wrapper, so it cannot affect layout or cropping.
     Runs sitewide since this script loads on every template, not just
     the homepage.
     ------------------------------------------------------------------ */
  function initBlurUp() {
    if (reduceMotion) return;
    var sections = document.querySelectorAll(".shopify-section");
    sections.forEach(function (section) {
      if (/header|footer|aside/i.test(section.id)) return;
      var imgs = section.querySelectorAll("img");
      imgs.forEach(function (img) {
        img.classList.add("lmk-blurup");
        function reveal() {
          img.classList.add("lmk-blurup-in");
        }
        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          img.addEventListener("load", reveal, { once: true });
          img.addEventListener("error", reveal, { once: true });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Sticky header solidify — once the page scrolls past the top, the
     header picks up a solid background and a soft shadow so it reads
     clearly over whatever content is beneath it. Only toggles a class
     on the header itself (background/shadow only, never size or
     spacing), so it can't shift layout. Applies on every page since the
     header is shared sitewide.
     ------------------------------------------------------------------ */
  function initHeaderSolidify() {
    var header =
      document.querySelector("header") ||
      document.querySelector('.shopify-section[id*="header" i]');
    if (!header) return;

    var threshold = 40;
    var ticking = false;

    function update() {
      if (window.scrollY > threshold) {
        header.classList.add("lmk-header-solid");
      } else {
        header.classList.remove("lmk-header-solid");
      }
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ------------------------------------------------------------------
     Custom magnetic cursor (desktop only)
     ------------------------------------------------------------------ */
  function initCursor() {
    document.documentElement.classList.add("lmk-has-cursor");

    var dot = document.createElement("div");
    dot.id = "lmk-cursor-dot";
    var ring = document.createElement("div");
    ring.id = "lmk-cursor-ring";
    var label = document.createElement("span");
    label.textContent = "VIEW";
    ring.appendChild(label);
    document.body.appendChild(dot);
    document.body.appendChild(ring);

    var mouseX = window.innerWidth / 2,
      mouseY = window.innerHeight / 2;
    var ringX = mouseX,
      ringY = mouseY;

    window.addEventListener(
      "mousemove",
      function (e) {
        mouseX = e.clientX;
        mouseY = e.clientY;
        dot.style.transform =
          "translate3d(" + mouseX + "px," + mouseY + "px,0) translate3d(-50%,-50%,0)";
      },
      { passive: true }
    );

    function raf() {
      ringX += (mouseX - ringX) * 0.16;
      ringY += (mouseY - ringY) * 0.16;
      ring.style.transform =
        "translate3d(" + ringX + "px," + ringY + "px,0) translate3d(-50%,-50%,0)";
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    var viewTargets = document.querySelectorAll(
      '[id*="hotspot" i] img, [id*="hero_slideshow" i] img, [id*="hero-slideshow" i] img, [id*="hotspot" i] a, [id*="hero_slideshow" i] a'
    );
    viewTargets.forEach(function (el) {
      el.addEventListener("mouseenter", function () {
        ring.classList.add("lmk-cursor-view");
      });
      el.addEventListener("mouseleave", function () {
        ring.classList.remove("lmk-cursor-view");
      });
    });

    // Magnetic pull on buttons
    var buttons = document.querySelectorAll(
      '.shopify-section a[class*="button"], .shopify-section button[class*="button"], .shopify-section .button'
    );
    buttons.forEach(function (btn) {
      btn.addEventListener("mousemove", function (e) {
        var rect = btn.getBoundingClientRect();
        var mx = e.clientX - rect.left - rect.width / 2;
        var my = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = "translate(" + mx * 0.25 + "px," + my * 0.25 + "px)";
      });
      btn.addEventListener("mouseleave", function () {
        btn.style.transform = "";
      });
    });
  }

  /* ------------------------------------------------------------------
     3D tilt on portfolio / hotspot images (kept from previous pass)
     ------------------------------------------------------------------ */
  function initTilt() {
    var targets = document.querySelectorAll(
      '[id*="hotspot" i] img, [id*="hero_slideshow" i] img, [id*="hero-slideshow" i] img'
    );
    targets.forEach(function (img) {
      img.classList.add("lmk-tilt");
      var host = img.closest("[id]") || img.parentElement;
      var raf = null;

      function onMove(e) {
        var rect = img.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        var rx = (py - 0.5) * -8;
        var ry = (px - 0.5) * 8;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          img.style.setProperty("--lmk-rx", rx.toFixed(2) + "deg");
          img.style.setProperty("--lmk-ry", ry.toFixed(2) + "deg");
          img.style.setProperty("--lmk-scale", "1.03");
        });
      }
      function onLeave() {
        if (raf) cancelAnimationFrame(raf);
        img.style.setProperty("--lmk-rx", "0deg");
        img.style.setProperty("--lmk-ry", "0deg");
        img.style.setProperty("--lmk-scale", "1");
      }
      (host || img).addEventListener("mousemove", onMove, { passive: true });
      (host || img).addEventListener("mouseleave", onLeave, { passive: true });
    });
  }

})();
